import { JSX } from 'react';
import { appContext } from '../../../../AppContext';
import { IOntologyNode } from './EditOntology';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import { PagedResponse } from "@iconics/core";
import { IOntologyNodeData } from './DataModels';
import { GraphStyleHelper } from '../../../../Common/GraphStyleHelper';
import { Switch } from '@serendie/ui';
import { Validator } from '@iconics/core';
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { MultilingualTextField } from "@iconics/components";
import { Button, ButtonBar, Card, Dialog, DialogContent, DialogFooter, GridContainer, GridItem, RightButtons, Select, TextArea, TextField } from '@iconics/components';
import { ApiConstants } from "@iconics/core";

class EditOntologyNodeDialog extends IbssComponent<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private graphStyleHelper = new GraphStyleHelper();
    private get labels() { return appContext().labels; }
    private languageValuesHelper: LanguageValuesHelper;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state =
        {
            id: '',
            enabled: false,
            name: '',
            nameError: '',
            displayName: this.languageValuesHelper.default(),
            recursive: false,
            required: false,
            parentId: '',
            concurrencyToken: '',
            parentOntologyNodes: [],
            styling: '',
            stylingError: '',
        };
    }

    public componentDidMount(): void
    {
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();
        this.validator
            .addInput('styling', () => this.state.styling, i => this.setState({ stylingError: i }))
            .isRequired()
            .customRule(value => this.graphStyleHelper.isValid(value), { errorLabel: this.labels.Platform_Ontology_EditOntologyNodeInvalidStyling });
    }

    public async componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>): Promise<void>
    {
        if (!prevProps.open && this.props.open)
        {
            await this.handleOpen();
        }
    }

    private async handleOpen(): Promise<void>
    {
        await this.loading(async () =>
        {
            const node = this.props.selectedOntologyNode;
            const isNew = (node == null);

            const response = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.get<PagedResponse<IOntologyNodeData[]>>({
                tenancy: appContext().tenantId,
                id: this.props.ontologyId,
                top: ApiConstants.largeODataTop,
            });

            const parentNodes: IParentOntologyNode[] = response.value.map(x => ({
                id: x.Id,
                name: x.Name,
            }));

            this.setState({
                id: (isNew ? '' : node.id),
                enabled: (isNew ? false : node.isEnabled),
                recursive: (isNew ? false : node.isRecursive),
                required: (isNew ? false : node.isRequired),
                parentId: (isNew ? '' : node.parentId),
                name: (isNew ? '' : node.name),
                displayName: (isNew? this.languageValuesHelper.default() : node.displayName),
                concurrencyToken: (isNew ? '' : node.etag),
                parentOntologyNodes: parentNodes,
                styling: this.graphStyleHelper.toJsonString(node?.styling),
            });
            this.validator.resetAllValidation();
        });
    }

    private handleCancel(): void
    {
        this.props.onClose();
    }

    private handleSave(): void
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        if (this.props.selectedOntologyNode == null)
        {
            this.create()
        }
        else
        {
            this.update()
        }
    }

    private async create(): Promise<void>
    {
        try
        {
            const response = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.post<IOntologyNodeData>({
                id: this.props.ontologyId,
                tenancy: this.props.tenancy,
                body: {
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    IsEnabled: this.state.enabled ? 'Yes' : 'No',
                    IsRecursive: this.state.recursive ? 'Yes' : 'No',
                    IsRequired: this.state.required ? 'Yes' : 'No',
                    Parent_ID: this.state.parentId,
                    Styling: this.graphStyleHelper.parse(this.state.styling),
                }
            });
            this.props.onSave(response.Id);

        } catch (error)
        {
            console.error(error);
        }
    }

    private async update(): Promise<void>
    {
        try
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.byOntologynodeid.put({
                ontologynodeid: this.state.id,
                id: this.props.ontologyId,
                tenancy: this.props.tenancy,
                body: {
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    IsRecursive: this.state.recursive ? 'Yes' : 'No',
                    IsRequired: this.state.required ? 'Yes' : 'No',
                    IsEnabled: this.state.enabled ? 'Yes' : 'No',
                    Parent_ID: this.state.parentId,
                    Styling: this.graphStyleHelper.parse(this.state.styling),
                    '@odata.etag': this.state.concurrencyToken
                }
            });
            this.props.onSave(this.state.id,);

        } catch (error)
        {
            console.error(error);
        }
    }

    public render(): JSX.Element
    {
        const parentOptions = [
            {
                label: this.labels.Platform_OntologyNode_Parent_None,
                value: '',
            },
            ...this.state.parentOntologyNodes
                .filter(x => x.id !== this.props.selectedOntologyNode?.id)
                .map(x => ({
                    label: x.name,
                    value: x.id,
                })),
        ];

        return (
            <Dialog
                isOpen={this.props.open}
                onOpenChange={() => this.handleCancel()}
            >
                <DialogContent>
                    <Card
                        title={this.labels.Platform_Ontology_EditOntologyNodeTitle}
                        subTitle={this.labels.Platform_Ontology_EditOntologyNodeSubTitle}
                        details={this.labels.Platform_Ontology_EditOntologyNodeDetails}
                    >
                        <GridContainer>
                            <GridItem expanded={6}>
                                <TextField
                                    required
                                    label={this.labels.OntologyNode_Name}
                                    placeholder={this.labels.OntologyNode_Name}
                                    value={this.state.name}
                                    onChange={(e) => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                                    invalid={!!this.state.nameError}
                                    invalidMessage={this.state.nameError}
                                />
                            </GridItem>
                            <GridItem expanded={6}>
                                <MultilingualTextField
                                    label={this.labels.OntologyNode_DisplayName}
                                    placeholder={this.labels.OntologyNode_DisplayName}
                                    value={this.state.displayName}
                                    onChange={(value) => this.setState({ displayName: value })}
                                    localisation={appContext().localisation}
                                />
                            </GridItem>
                            <GridItem expanded={6}>
                                <Select
                                    label={this.labels.OntologyNode_Parent}
                                    placeholder={this.labels.OntologyNode_Parent}
                                    items={parentOptions}
                                    value={[this.state.parentId]}
                                    onSelect={e => this.setState({ parentId: e.value })}
                                />
                            </GridItem>
                            <GridItem>
                                <TextArea
                                    label={this.labels.OntologyNode_Styling}
                                    placeholder={this.labels.OntologyNode_Styling}
                                    value={this.state.styling}
                                    rows={5}
                                    onChange={e => this.setState({ styling: e.target.value }, () => this.validator.validate('styling'))}
                                    invalid={!!this.state.stylingError}
                                    invalidMessage={this.state.stylingError}
                                />
                            </GridItem>
                            <GridItem>
                                <Switch
                                    label={this.labels.OntologyNode_IsRecursive}
                                    checked={this.state.recursive}
                                    onCheckedChange={e => this.setState({ recursive: e.checked as boolean })}
                                />
                            </GridItem>
                            <GridItem>
                                <Switch
                                    label={this.labels.OntologyNode_IsRequired}
                                    checked={this.state.required}
                                    onCheckedChange={e => this.setState({ required: e.checked as boolean })}
                                />
                            </GridItem>
                            <GridItem>
                                <Switch
                                    label={this.labels.OntologyNode_IsEnabled}
                                    checked={this.state.enabled}
                                    onCheckedChange={e => this.setState({ enabled: e.checked as boolean })}
                                />
                            </GridItem>
                        </GridContainer>
                    </Card>
                </DialogContent>
                <DialogFooter>
                    <ButtonBar>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.handleCancel()}
                            >
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handleSave()}
                                disabled={!this.validator.isValid}
                            >
                                {this.labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </DialogFooter>
            </Dialog>
        )
    }
}

export default EditOntologyNodeDialog;

export interface IProps
{
    open: boolean;
    onClose: () => void;
    onSave: (id: string) => void;
    selectedOntologyNode: IOntologyNode | null;
    tenancy: string;
    ontologyId: string;
}

export interface IState
{
    id: string;
    enabled: boolean;
    name: string,
    nameError: string;
    displayName: ILanguageValues;
    recursive: boolean,
    required: boolean,
    parentId: string,
    concurrencyToken: string
    parentOntologyNodes: IParentOntologyNode[],
    styling: string;
    stylingError: string;
}

interface IParentOntologyNode
{
    id: string;
    name: string;
}
